import React, { useEffect, useContext, Suspense, lazy } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { EnvContext, ErrorTemplate, Progress, shortcutListener } from 'next-common-lib';
import { i18nChangeLanguage as i18nChangeLanguageUserLib, ServiceEnvContext } from 'next-user-lib';
import { i18nPimsChangeLanguage as i18nChangeLanguagePimsLib } from 'next-pims-lib';
import i18nChangeLanguage from 'i18n/i18nChangeLanguage';
import { getLanguage } from 'utils/cookies';
import Alert from 'components/Common/Popup/Alert';
import Confirm from 'components/Common/Popup/Confirm';
import ConfirmDelete from 'components/Common/Popup/ConfirmDelete';
import Loading from 'components/Common/Popup/Loading';

import { getShortcutIframeInterface } from 'utils/shortcutManager';
import * as userinfoActions from 'redux/ducks/userInfo/userInfoDucks';

const FindPassword = lazy(() => import('pages/FindPassword'));
const PasswordChangeForExchange = lazy(() => import('pages/PasswordChangeForExchange'));
const MyProfileChangePhoto = lazy(() => import('pages/MyProfileChangePhoto'));
const PersonalInformation = lazy(() => import('pages/PersonalInformation'));
const PrivacyPolicy = lazy(() => import('pages/PrivacyPolicy'));
const PrivacyPolicyHtml = lazy(() => import('pages/PrivacyPolicyHtml'));
const Profile = lazy(() => import('pages/Profile'));
const Search = lazy(() => import('pages/Search'));
const TempSearch = lazy(() => import('pages/TempSearch'));
const ServiceDesk = lazy(() => import('pages/ServiceDesk'));
const GdriveServiceDesk = lazy(() => import('pages/GdriveServiceDesk'));
const Setting = lazy(() => import('pages/Setting'));
const Signup = lazy(() => import('pages/Signup'));
const Landing = lazy(() => import('pages/Landing'));

const AuthenticationComponent = ({ children }) => {
  const { setEnvTheme, setEnvSkinCode, setEnvLanguage } = useContext(EnvContext);
  const { isKnox } = useContext(ServiceEnvContext);
  const userInfo = useSelector(state => state.userInfo.userInfo);
  const roles = useSelector(state => state.userInfo.roles);
  const disasterMode = useSelector(state => state.userInfo.disasterMode);
  const isKnoxEnvValueLoaded = useSelector(state => state.userInfo.isKnoxEnvValueLoaded);
  const dispatch = useDispatch();
  const isException = !isKnox && useLocation().pathname.indexOf('/privacy-policy') > -1; // 브리티면서 개인정보처리방침일경우 noSession으로 제공됨에 따라 해당 로직 추가

  useEffect(() => {
    const interfaceFn = (action, payload) => {
      const shortcutIframeInterface = getShortcutIframeInterface('PROFILE_SETTING');
      if (action === 'shortcut') {
        shortcutIframeInterface.sendToMaster(action, payload);
      }
    };
    shortcutListener.addShortcutListener(interfaceFn);
  }, []);

  useEffect(() => {
    setEnvLanguage(getLanguage(), true, { initLanguage: getLanguage() });

    const changeLanguage = userInfo.BIZCLSFCD?.length > 0 ? `${getLanguage()}_${userInfo.BIZCLSFCD}` : getLanguage();
    i18nChangeLanguage(changeLanguage);
    i18nChangeLanguageUserLib(changeLanguage);
    i18nChangeLanguagePimsLib(changeLanguage);

    setEnvSkinCode(userInfo.THEME);
    if (userInfo.DARKMODEYN === 'Y') {
      setEnvTheme('dark');
    } else {
      setEnvTheme('light');
    }

    const userSession_USABLESVCLIST = userInfo.USABLESVCLIST;
    window.COM_UTILS.isValidTarget(userSession_USABLESVCLIST);
  }, [userInfo]);

  useEffect(() => {
    dispatch(userinfoActions.setIsKnoxEnv(isKnox));
    if (!isException) {
      dispatch(userinfoActions.getUserInfo());
      dispatch(userinfoActions.getRoles());
      dispatch(userinfoActions.getDisasterRecoveryMode());
    }
  }, []);

  return (isKnoxEnvValueLoaded && isException) ? <>{children}</> : <>{userInfo && roles && disasterMode !== null && children}</>;
};

const AuthenticateRoute = ({ children }) => {
  return <AuthenticationComponent>{children}</AuthenticationComponent>;
};

const NonAuthenticationComponent = ({ children }) => {
  const { setEnvLanguage } = useContext(EnvContext);
  const { isKnox } = useContext(ServiceEnvContext);
  const dispatch = useDispatch();
  const disasterMode = useSelector(state => state.userInfo.disasterMode);

  useEffect(() => {
    dispatch(userinfoActions.setIsKnoxEnv(isKnox));
    dispatch(userinfoActions.getNoSessionDisasterRecoveryMode());
    setEnvLanguage(getLanguage(), true, { initLanguage: getLanguage() });
  }, []);

  return <>{disasterMode !== null && children}</>;
};

const NonAuthenticateRoute = ({ children }) => {
  return <NonAuthenticationComponent>{children}</NonAuthenticationComponent>;
};

const App = () => {
  return (
    <>
      <BrowserRouter>
        <Suspense fallback={<Progress componentType="loading" />}>
          <Routes>
            <Route path="/employeeapp/my-profile/change-photo/" element={<AuthenticateRoute><MyProfileChangePhoto /></AuthenticateRoute>} />
            <Route path="/employeeapp/personal-information/:menu" element={<AuthenticateRoute><PersonalInformation /></AuthenticateRoute>} />
            <Route path="/employeeapp/privacy-policy/" element={<AuthenticateRoute><PrivacyPolicy /></AuthenticateRoute>} />
            <Route path="/employeeapp/profile/:menu" element={<AuthenticateRoute><Profile /></AuthenticateRoute>} />
            <Route path="/employeeapp/search/:type" element={<AuthenticateRoute><Search /></AuthenticateRoute>} />
            <Route path="/employeeapp/temp-search/:type" element={<AuthenticateRoute><TempSearch /></AuthenticateRoute>} />
            <Route path="/employeeapp/setting/:menu" element={<AuthenticateRoute><Setting /></AuthenticateRoute>} />
            <Route path="/employeeapp/landing/" element={<AuthenticateRoute><Landing /></AuthenticateRoute>} />
            <Route path="/employeeapp/find-password/" element={<NonAuthenticateRoute><FindPassword /></NonAuthenticateRoute>} />
            <Route path="/employeeapp/password-change-for-exchange/" element={<NonAuthenticateRoute><PasswordChangeForExchange /></NonAuthenticateRoute>} />
            <Route path="/employeeapp/service-desk/" element={<NonAuthenticateRoute><ServiceDesk /></NonAuthenticateRoute>} />
            <Route path="/employeeapp/gdrive-service-desk/" element={<NonAuthenticateRoute><GdriveServiceDesk /></NonAuthenticateRoute>} />
            <Route path="/employeeapp/signup/" element={<NonAuthenticateRoute><Signup /></NonAuthenticateRoute>} />
            <Route
              path="/employeeapp/privacy-policy-html/:fileName"
              element={<NonAuthenticateRoute><PrivacyPolicyHtml /></NonAuthenticateRoute>}
            />
            <Route path="*" element={<ErrorTemplate refTitle="Page Not Found" askBtn refCode="404" service="Employee" />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Alert />
      <Confirm />
      <ConfirmDelete />
      <Loading />
    </>
  );
};
export default App;
